/**
 * Link mode for a GENERIC (no patient selected) form or packet link — "Who is this link for?"
 *
 *   'new'      — today's generic link: opens the form directly; the submission arrives "Not linked".
 *   'existing' — the patient first confirms date of birth + first + last name on the public
 *                IdentifyPatient page, then enters a 6-digit code sent to the phone/email on file;
 *                after that the link continues exactly like a verified patient-specific link
 *                (prefilled form, submission auto-linked to the patient).
 *
 * Plan: Documents/forms-existing-patient-links.md (D1). Links made before this shipped resolve as 'new'.
 *
 * ⛔ Keep this file IMPORT-FREE: cypress/node-tests/forms-link-mode.test.js executes it directly.
 */

const NEW = 'new';
const EXISTING = 'existing';

/**
 * Default mode for the Get Link modal.
 *   any NP_FORM in the form/packet        → 'new'      (a new-patient packet is for new patients)
 *   else any MED_HX_UPDATE                → 'existing' (a medical-history update is for patients on file)
 *   else (OTHER, unknown, nothing)        → 'new'      (today's behaviour)
 *
 * @param {{ formType?: string, packetFormTypes?: string[] }} input
 * @returns {'new'|'existing'}
 */
function defaultLinkMode(input) {
    const opts = input || {};
    const types = [];
    if (opts.formType) types.push(opts.formType);
    if (Array.isArray(opts.packetFormTypes)) {
        opts.packetFormTypes.forEach(function (t) { if (t) types.push(t); });
    }
    if (types.indexOf('NP_FORM') !== -1) return NEW;
    if (types.indexOf('MED_HX_UPDATE') !== -1) return EXISTING;
    return NEW;
}

/**
 * Anything that is not exactly 'existing' is 'new' — a missing/unknown value must never send a
 * patient through identification by accident, nor skip it for a link that asked for it.
 *
 * @param {*} value
 * @returns {'new'|'existing'}
 */
function normalizeLinkMode(value) {
    return value === EXISTING ? EXISTING : NEW;
}

/**
 * What the Get Link modal sends, from its tab. The modal has two tabs (2026-10-07):
 *   'patient' — "Send to a patient": a patient from the search is REQUIRED; the link is patient-specific
 *               and the mode is omitted. Staff once sent blank "New patients" links to patients already
 *               in Open Dental (new to the practice, not new to OD) — this tab is where they belong.
 *   'website' — "Website or QR code": never a patient; the mode says who will use the link.
 * Anything that is not exactly 'website' is the patient tab, so a bad value can never produce a
 * patient-less link by accident.
 *
 * @param {{ linkTarget?: string, patNum?: *, linkMode?: * }} input
 * @returns {{ patNum: number|null, linkMode: ('new'|'existing'|undefined) } | { error: string }}
 */
function buildLinkRequest(input) {
    const opts = input || {};
    if (opts.linkTarget === 'website') {
        return { patNum: null, linkMode: normalizeLinkMode(opts.linkMode) };
    }
    const patNum = Number(opts.patNum);
    if (!Number.isInteger(patNum) || patNum <= 0) {
        return { error: 'Pick a patient first' };
    }
    return { patNum: patNum, linkMode: undefined };
}

export { defaultLinkMode, normalizeLinkMode, buildLinkRequest };
